/**
 * 声音系统 V2 词表(2026-09-08,A/B/C 三批同稿契约)。
 *
 * ★放 entities/dh-profile 不放 features/digital-human:陪练场景表单(features/coaching)
 *   的「客户口音 / 说话风格」用**同一份词表** —— features 之间禁止互相 import
 *   (ADR-0013 单向核心),共用词表只能下沉到 entities。
 * ★slug 是跨仓契约键(ai/api/console 同稿),中文名只是展示 —— 改展示名可以,改 slug
 *   要三仓一起动。
 * ★能力边界:方言/多语言/演绎方式只对「我的声纹」(克隆声线,走新模型)真实生效;
 *   预置音色仍是旧引擎 —— 控件的显隐由消费方按 voiceKind 闸,词表本身不带这个判断。
 */

/** 语言(16 项,契约钉死)。value 随请求 voiceLanguage 上送,默认 zh。 */
export const DH_VOICE_LANGUAGES = [
  { value: "zh", label: "中文" },
  { value: "en", label: "English" },
  { value: "ja", label: "日本語" },
  { value: "ko", label: "한국어" },
  { value: "ru", label: "Русский" },
  { value: "fr", label: "Français" },
  { value: "de", label: "Deutsch" },
  { value: "pt", label: "Português" },
  { value: "th", label: "ไทย" },
  { value: "id", label: "Bahasa Indonesia" },
  { value: "vi", label: "Tiếng Việt" },
  { value: "es", label: "Español" },
  { value: "it", label: "Italiano" },
  { value: "ms", label: "Bahasa Melayu" },
  { value: "fil", label: "Filipino" },
  { value: "ar", label: "العربية" },
] as const;

export const DH_DEFAULT_VOICE_LANGUAGE = "zh";

/**
 * 口音/方言(契约钉死;仅 voiceLanguage=zh 可非空)。三态(0908 用户「选普通话就没了
 * 自己的味道」):null=**跟随原声**(新默认,ai 侧点名保持克隆样本里的天然口音);
 * "mandarin"=用户明确要标准普通话(此前它占着 null 的默认位,把带口音的声线掰标准);
 * 其余 20 项=方言。
 */
export const DH_VOICE_DIALECTS = [
  { value: "mandarin", label: "普通话" },
  { value: "yueyu", label: "粤语" },
  { value: "sichuan", label: "四川话" },
  { value: "chongqing", label: "重庆话" },
  { value: "shanghai", label: "上海话" },
  { value: "dongbei", label: "东北话" },
  { value: "henan", label: "河南话" },
  { value: "shandong", label: "山东话" },
  { value: "shaanxi", label: "陕西话" },
  { value: "guizhou", label: "贵州话" },
  { value: "hunan", label: "湖南话" },
  { value: "hubei", label: "湖北话" },
  { value: "jiangxi", label: "江西话" },
  { value: "yunnan", label: "云南话" },
  { value: "hebei", label: "河北话" },
  { value: "shanxi", label: "山西话" },
  { value: "zhejiang", label: "浙江话" },
  { value: "ningbo", label: "宁波话" },
  { value: "ningxia", label: "宁夏话" },
  { value: "qingdao", label: "青岛话" },
  { value: "gansu", label: "甘肃话" },
] as const;

/** 平铺成 chip 的高频方言(其余收进「更多方言」下拉,顺序即展示序)。 */
export const DH_FEATURED_DIALECTS = [
  "mandarin",
  "yueyu",
  "sichuan",
  "shanghai",
  "dongbei",
] as const;

/**
 * 演绎方式(6 项;默认 natural)。desc 是卡面一句话,不上送。
 * ★1006:natural 改名「原声风格」—— ai 侧该档不下发任何演绎指令(最像本人);值 natural 不变。
 */
export const DH_VOICE_STYLES = [
  { value: "natural", label: "原声风格", desc: "不加演绎指令,最像本人" },
  { value: "sales", label: "营销带货", desc: "抓人不浮夸,结尾有行动感" },
  { value: "professional", label: "专业讲解", desc: "可信、清晰、稳重" },
  { value: "ceo", label: "老板口播", desc: "像负责人亲自介绍业务" },
  { value: "brand", label: "品牌宣传", desc: "节奏舒展,有质感" },
  { value: "expressive", label: "情绪演绎", desc: "随内容起伏,适合剧情" },
] as const;

export const DH_DEFAULT_VOICE_STYLE = "natural";

export type DhVoiceStyleValue = (typeof DH_VOICE_STYLES)[number]["value"];

/** slug → 中文名(展示/回显用;认不出的 slug 原样返回,不编一个名)。 */
export function dialectLabelOf(slug: string | null | undefined): string {
  if (!slug) return "跟随原声";
  return DH_VOICE_DIALECTS.find((d) => d.value === slug)?.label ?? slug;
}

export function voiceStyleLabelOf(value: string | null | undefined): string {
  if (!value) return "";
  return DH_VOICE_STYLES.find((s) => s.value === value)?.label ?? value;
}

/** 词表校验器(草稿/返稿回填用:形状不对退默认,不整包作废)。 */
export function isKnownVoiceLanguage(v: unknown): v is string {
  return typeof v === "string" && DH_VOICE_LANGUAGES.some((l) => l.value === v);
}
export function isKnownVoiceDialect(v: unknown): v is string {
  return typeof v === "string" && DH_VOICE_DIALECTS.some((d) => d.value === v);
}
export function isKnownVoiceStyle(v: unknown): v is string {
  return typeof v === "string" && DH_VOICE_STYLES.some((s) => s.value === v);
}

/**
 * 声音 V2 锁定提示(1006 文案统一,创建页与详情页「换音色」共用一份)。
 * 预置音色是旧引擎;旧版声纹(supportsTuning===false)是 cosyvoice 时代录的 —— 两者三件套都不生效。
 */
export const PRESET_VOICE_LOCK_TEXT =
  "预置音色暂不支持方言、多语言与演绎方式 —— 选我的声纹,一次录音说 20 种方言和 16 种语言";
export const LEGACY_PROFILE_LOCK_TEXT =
  "这条是旧版声纹,方言、多语言与演绎方式不生效 —— 重新录一次即可解锁";
/** 演绎区说明补句(1006):非「原声风格」档的取舍。 */
export const VOICE_STYLE_TRADEOFF_TEXT = "选其它演绎方式会让声音更有表现力,但与本人相似度略降。";
